'use client';
import React from 'react';
import {Dialog, DialogContent, DialogTitle} from './dialog';
import {Button} from './Button';
import {pricing} from '@features/subscription/pricing';

type PlanType = 'plus' | 'pro' | 'team-plus' | 'team-pro';

interface AnnualUpgradeConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
  isLoading: boolean;
  planType: PlanType;
  seatsCount?: number;
  /** Override the "from" price label with the actual billed amount (e.g. "$10/mo") */
  currentPriceLabel?: string;
}

const planConfig: Record<
  PlanType,
  {name: string; monthlyPrice: number; yearlyPrice: number}
> = {
  plus: {
    name: 'Plus',
    monthlyPrice: pricing.individual.plus.monthly,
    yearlyPrice: pricing.individual.plus.yearly,
  },
  pro: {
    name: 'Pro',
    monthlyPrice: pricing.individual.pro.monthly,
    yearlyPrice: pricing.individual.pro.yearly,
  },
  'team-plus': {
    name: 'Team Plus',
    monthlyPrice: pricing.team.plus.monthly,
    yearlyPrice: pricing.team.plus.yearly,
  },
  'team-pro': {
    name: 'Team Pro',
    monthlyPrice: pricing.team.pro.monthly,
    yearlyPrice: pricing.team.pro.yearly,
  },
};

export function AnnualUpgradeConfirmDialog({
  open,
  onOpenChange,
  onConfirm,
  isLoading,
  planType,
  seatsCount = 1,
  currentPriceLabel,
}: AnnualUpgradeConfirmDialogProps) {
  const config = planConfig[planType];
  const isTeam = planType === 'team-plus' || planType === 'team-pro';
  const monthlyPrice = `$${config.monthlyPrice * (isTeam ? seatsCount : 1)}`;
  const yearlyPrice = config.yearlyPrice * (isTeam ? seatsCount : 1);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogTitle>Change plan</DialogTitle>
        <div className='flex justify-between'>
          <span className='font-semibold'>Plan</span>
          {config.name} Monthly → {config.name} Yearly
        </div>
        <div className='flex justify-between'>
          <span className='font-semibold'>Price</span>
          <span>
            {currentPriceLabel ?? `${monthlyPrice}/mo`} → ${yearlyPrice}/year
          </span>
        </div>
        <div className='text-sm text-muted-foreground'>
          Only the difference will be charged. Your unused balance is deducted
          automatically.
        </div>
        <Button disabled={isLoading} onClick={onConfirm}>
          {isLoading ? 'Upgrading…' : 'Confirm upgrade'}
        </Button>
      </DialogContent>
    </Dialog>
  );
}
